import { FastForward, Plus } from '@styled-icons/boxicons-regular';
import { Edit, Trash } from '@styled-icons/feather';
import { AnimatedText } from '@typedream/components';
import type { LeafAnimation as LeafAnimationType, TextLeaf } from '@typedream/core';
import {
  focusEditor,
  getLeafNode,
  getRange,
  isCollapsed,
  select,
} from '@udecode/plate-core';
import classNames from 'classnames';
import { memo, useState } from 'react';
import { useTPlateEditorRef } from '../../../../plate';
import {
  addLeafAnimationAtSelection,
  removeLeafAnimationAtSelection,
} from '../../../../nodes/animatedText';
import { useDreamEditorStore } from '../../../../plugins/store';
import { useOutsideEvent } from '../../../../utils/eventAlerter';
import toolbarStyles from '../../SideToolbar.module.css';
import styles from './LeafAnimation.module.css';

const displayedAnimations: { name: string; animation: LeafAnimationType }[] = [
  {
    name: 'Scroll Words',
    animation: {
      type: 'scroll_words',
      words: ['Fun', 'Cool', 'Nice'],
    },
  },
];

function LeafAnimation() {
  const editor = useTPlateEditorRef();

  const [isShowSelectAnimation, setisShowSelectAnimation] = useState(false);

  const leaf = useDreamEditorStore(() => {
    if (!editor.selection) return undefined;
    return getLeafNode(editor, editor.selection.focus)[0] as TextLeaf;
  });

  const [editingWordIndex, seteditingWordIndex] = useState<number>();

  const menuRef = useOutsideEvent<HTMLDivElement>('mousedown', () => {
    setisShowSelectAnimation(false);
  });

  const handleSelectAnimation = (animation: LeafAnimationType) => {
    if (!leaf) return;
    if (editor.selection && isCollapsed(editor.selection)) {
      select(editor, getRange(editor, editor.selection.focus.path));
    }
    addLeafAnimationAtSelection(editor, animation);
    focusEditor(editor);
  };

  const handleRemoveAnimation = () => {
    if (!leaf) return;
    if (editor.selection && isCollapsed(editor.selection)) {
      select(editor, getRange(editor, editor.selection.focus.path));
    }
    removeLeafAnimationAtSelection(editor);
    focusEditor(editor);
  };

  const renderSelectAnimation = () => (
    <div className={classNames(toolbarStyles.grid)}>
      {displayedAnimations.map((item) => (
        <div
          key={item.name}
          className="is-flex is-flex-direction-column is-align-items-center is-justify-content-center"
          onClick={() => {
            handleSelectAnimation(item.animation);
            setisShowSelectAnimation(false);
          }}
          style={{ cursor: 'pointer' }}
          role="none"
        >
          <div className={styles.animationContainer}>
            <span className={styles.animatedText}>
              <AnimatedText animation={item.animation} style={undefined} />
            </span>
          </div>
          <div className="is-size-7" style={{ color: 'var(--text-lighter)' }}>
            {item.name}
          </div>
        </div>
      ))}
    </div>
  );

  const renderAnimationOptions = () => (
    <div className={toolbarStyles.card}>
      <div
        className={classNames(
          'is-flex is-align-items-center is-justify-content-space-between',
        )}
      >
        <div className={styles.currentAnimation}>
          <FastForward size={20} />
          {leaf?.animation?.type && leaf.animation.type.replace('_', ' ')}
        </div>
        <div className="is-flex is-align-items-center">
          <button
            className={toolbarStyles.icon}
            type="button"
            onClick={handleRemoveAnimation}
          >
            <Trash size={16} />
          </button>
        </div>
      </div>

      {/* WORDS OPTIONS */}
      <div className="mt-2">
        <label className={toolbarStyles.subLabel} htmlFor="#">
          Words
        </label>

        <div className={classNames(toolbarStyles.card, 'mb-1')}>
          <div style={{ color: 'var(--text-lighter)' }}>{leaf?.text}</div>
        </div>
        {leaf?.animation?.words?.map((word, index, words) => (editingWordIndex === index ? (
          <form
            className={classNames(toolbarStyles.menuGroup, 'mb-1')}
            onSubmit={(e) => {
              e.preventDefault();
              const value = new FormData(e.target as HTMLFormElement).get(
                'word',
              ) as string;
              handleSelectAnimation({
                ...leaf.animation!,
                words: [
                  ...words.slice(0, editingWordIndex),
                  value,
                  ...words.slice(editingWordIndex + 1),
                ],
              });
              seteditingWordIndex(undefined);
            }}
          >
            <input
              type="text"
              name="word"
              defaultValue={word}
              placeholder="Paste link or search page"
              className={styles.wordInput}
            />

            <button
              className={classNames(toolbarStyles.menuItem, 'p-2')}
              type="submit"
            >
              Save
            </button>
          </form>
        ) : (
          <div className={classNames(toolbarStyles.card, 'mb-1')}>
            <div className="is-flex is-align-items-center" style={{ gap: 0 }}>
              <div
                className={toolbarStyles.flex1}
                style={{ color: 'var(--text)' }}
              >
                {word}
              </div>
              <div className="is-flex is-align-items-center">
                <button
                  className={toolbarStyles.icon}
                  type="button"
                  onClick={() => seteditingWordIndex(index)}
                >
                  <Edit size={16} />
                </button>

                <button
                  className={toolbarStyles.icon}
                  type="button"
                  onClick={() => {
                    handleSelectAnimation({
                      ...leaf.animation!,
                      words: words.filter((_, i) => i !== index),
                    });
                    seteditingWordIndex(undefined);
                  }}
                >
                  <Trash size={18} />
                </button>
              </div>
            </div>
          </div>
        )))}
        <button
          className={classNames(
            'is-size-7',
            'is-flex',
            'is-align-items-center',
            styles.addWord,
          )}
          type="button"
          onClick={() => {
            handleSelectAnimation({
              ...leaf!.animation!,
              words: leaf!.animation!.words.concat(['Awesome']),
            });
          }}
        >
          <Plus size={18} className="mr-1" />
          Add word
        </button>
      </div>

      {/* TRIGGER, NO OPTIONS CURRENTLY */}
      <div className="mt-2">
        <label className={toolbarStyles.subLabel} htmlFor="#">
          Trigger
        </label>
        <div style={{ color: 'var(--text)' }}>None</div>
      </div>
    </div>
  );

  return (
    <div className={toolbarStyles.section}>
      <label htmlFor="#" className={toolbarStyles.label}>
        Text Animation
      </label>
      <div ref={menuRef} className={styles.menu}>
        {leaf?.animation ? (
          renderAnimationOptions()
        ) : (
          <button
            type="button"
            className={classNames(toolbarStyles.button)}
            onClick={() => setisShowSelectAnimation((prev) => !prev)}
          >
            Add Text Animation
          </button>
        )}
        {isShowSelectAnimation && (
          <div className={classNames(styles.floating)}>
            {renderSelectAnimation()}
          </div>
        )}
      </div>
    </div>
  );
}

export default memo(LeafAnimation);
